/**
 * Настройки платформы.
 *
 * Здесь лежит то, что платформа держит за собой и не отдаёт партнёру. Сейчас
 * одно: загрузка роликов файлом. Ролик на пятьдесят мегабайт, умноженный на
 * число акций, кончается диском сервера и счётом за исходящий трафик, а
 * ссылка на видеохостинг даёт на странице акции ровно тот же ролик и стоит
 * платформе ноль.
 *
 * Поэтому по умолчанию выключено, а исключения ставятся поимённо — на экране
 * брендов. Общий тумблер «разрешить всем» остаётся: когда файлы переедут в
 * объектное хранилище, запрет потеряет смысл.
 */
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Icon, Skeleton } from '@allswipes/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { Layout, ReadOnlyNote } from '../components/Layout';
import { useResource } from '../hooks';
import { useAdmin } from '../session';
import { useToast } from '../toast';

export function SettingsScreen() {
  const data = useResource(() => api.adminSettings(), []);
  const { canModerate } = useAdmin();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const videoUpload = data.data?.settings.video_upload === 'on';

  const toggle = async (key: string, on: boolean) => {
    setBusy(true);
    try {
      await api.adminSaveSetting(key, on ? 'on' : 'off');
      toast(on ? 'Включено' : 'Выключено');
      data.reload();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout
      title="Настройки платформы"
      subtitle="то, что решает платформа, а не партнёр за себя"
    >
      <ReadOnlyNote />

      {data.loading && <Skeleton count={2} height={96} />}

      {data.data && (
        <Card>
          <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
            <div style={{ minWidth: 0 }}>
              <div className="row" style={{ gap: 8, marginBottom: 4 }}>
                <span className="h2">Загрузка роликов партнёрами</span>
                <Badge tone={videoUpload ? 'good' : undefined}>
                  {videoUpload ? 'разрешена всем' : 'выключена'}
                </Badge>
              </div>
              <p className="sub">
                Файл до {50} МБ на акцию ложится на диск сервера, и каждый его просмотр — это
                платный исходящий трафик. Пока файлы лежат рядом с приложением, держи выключенным:
                партнёр вставляет ссылку на YouTube, Vimeo, Rutube или VK Видео, и ролик
                показывается на странице акции точно так же. Включать есть смысл, когда медиа
                переедет в объектное хранилище.
              </p>
              <p className="sub" style={{ marginTop: 8 }}>
                Отдельному бренду можно разрешить или запретить вопреки этой настройке — на
                экране <Link to="/brands">брендов</Link>. Загрузку фотографий призового фонда
                настройка не трогает: это доказательство, без него платформа не ставит отметку.
              </p>
            </div>
            <span className="spacer" />
            {canModerate && (
              <Button
                variant={videoUpload ? 'danger' : 'pri'}
                disabled={busy}
                onClick={() => void toggle('video_upload', !videoUpload)}
              >
                {videoUpload ? 'Выключить' : 'Разрешить всем'}
              </Button>
            )}
          </div>
        </Card>
      )}

      <Subscribers />

      <div className="note" style={{ marginTop: 14 }}>
        <Icon name="info" size={14} />
        <span>
          Запрет действует на сервере, а не только в кабинете: отказ приходит до чтения файла,
          поэтому выключенная загрузка не стоит платформе и мегабайта.
        </span>
      </div>
    </Layout>
  );
}

/**
 * Список рассылки.
 *
 * Подписка живёт отдельно от аккаунта: участие в акции само по себе согласием
 * на письма не является. Поэтому и выгрузка отдельная — её отдают почтовому
 * сервису, и попасть в неё должны только те, кто подписался сам.
 */
function Subscribers() {
  const { canModerate } = useAdmin();
  const toast = useToast();
  const overview = useResource(() => api.adminOverview(), []);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const { blob, name } = await api.exportSubscribers();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      toast('Список выгружен');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };

  const count = overview.data?.subscribers ?? 0;

  return (
    <Card style={{ marginTop: 18 }}>
      <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div className="row" style={{ gap: 8, marginBottom: 4 }}>
            <span className="h2">Подписка на новости</span>
            <Badge>{count}</Badge>
          </div>
          <p className="sub">
            Адреса из плашки в конце ленты. Отписавшиеся в выгрузку не попадают, а факт выгрузки
            уходит в журнал: список почт — это те же персональные данные, и кто его забрал,
            должно быть видно.
          </p>
        </div>
        <span className="spacer" />
        {canModerate && (
          <Button icon="download" disabled={busy || count === 0} onClick={() => void download()}>
            Выгрузить CSV
          </Button>
        )}
      </div>
    </Card>
  );
}
